import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import {
  addMembers,
  countMatching,
  createFolder,
  createList,
  deleteFolder,
  deleteList,
  duplicateList,
  getList,
  getListContacts,
  getListStats,
  getListUsage,
  getListCampaigns,
  listFolders,
  listLists,
  listListsPage,
  moveList,
  removeMember,
  sendToList,
  updateFolder,
  updateList,
  type ListCriterion,
  type ListInput,
  type ListPageParams,
} from '@/api/lists'
import type { SendMessageInput } from '@/api/messages'
import { useDebouncedValue } from '@/lib/useDebouncedValue'

/** Every lists query (table, picker, stats, one list, folders) starts with ['lists'] — one invalidation refreshes all. */
function invalidateLists(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ['lists'] })
}

/** Full list (for pickers/dropdowns that need every list). The table uses useListsPage instead. */
export function useLists() {
  return useQuery({ queryKey: ['lists'], queryFn: listLists })
}

/** Paginated/sorted/filtered lists for the table view; keeps the current page while the next loads. */
export function useListsPage(params: ListPageParams) {
  return useQuery({
    queryKey: ['lists', 'page', params],
    queryFn: () => listListsPage(params),
    placeholderData: keepPreviousData,
  })
}

export function useList(id?: string) {
  return useQuery({ queryKey: ['lists', 'one', id], queryFn: () => getList(id as string), enabled: !!id })
}

export function useListStats() {
  return useQuery({ queryKey: ['lists', 'stats'], queryFn: getListStats })
}

/** The list's campaigns with their stats — refetched now and then, as opens and clicks keep arriving. */
export function useListCampaigns(id?: string) {
  return useQuery({
    queryKey: ['lists', 'campaigns', id],
    queryFn: () => getListCampaigns(id as string),
    enabled: !!id,
    refetchInterval: 60_000,
  })
}

export function useListUsage(id?: string) {
  return useQuery({ queryKey: ['lists', 'usage', id], queryFn: () => getListUsage(id as string), enabled: !!id })
}

/** Live match count for criteria being edited (debounced); off until enabled. */
export function useCriteriaCount(criteria: ListCriterion[], enabled: boolean) {
  const debounced = useDebouncedValue(criteria, 500)
  return useQuery({
    queryKey: ['lists', 'count', debounced],
    queryFn: () => countMatching(debounced),
    enabled,
    placeholderData: keepPreviousData,
  })
}

export function useListFolders() {
  return useQuery({ queryKey: ['lists', 'folders'], queryFn: listFolders })
}

/** The contacts in a list (dynamic lists compute them on the fly). Lazy via {@code enabled}. */
export function useListContacts(listId?: string, enabled = true) {
  return useQuery({
    queryKey: ['lists', listId, 'contacts'],
    queryFn: () => getListContacts(listId as string),
    enabled: !!listId && enabled,
  })
}

export function useCreateList() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (input: ListInput) => createList(input), onSuccess: () => invalidateLists(qc) })
}

export function useUpdateList() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ListInput }) => updateList(id, input),
    onSuccess: () => {
      invalidateLists(qc)
      qc.invalidateQueries({ queryKey: ['contacts'] }) // new criteria change who the list's table shows
    },
  })
}

export function useDuplicateList() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: { name: string; folderId: string | null; keepMembers: boolean } }) =>
      duplicateList(id, input),
    onSuccess: () => invalidateLists(qc),
  })
}

export function useMoveList() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, folderId }: { id: string; folderId: string | null }) => moveList(id, folderId),
    onSuccess: () => invalidateLists(qc),
  })
}

export function useDeleteList() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (id: string) => deleteList(id), onSuccess: () => invalidateLists(qc) })
}

export function useCreateFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { name: string; color?: string | null }) => createFolder(input),
    onSuccess: () => invalidateLists(qc),
  })
}

export function useUpdateFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: { name: string; color?: string | null } }) => updateFolder(id, input),
    onSuccess: () => invalidateLists(qc),
  })
}

export function useDeleteFolder() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (id: string) => deleteFolder(id), onSuccess: () => invalidateLists(qc) })
}

export function useAddToList() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ listId, contactIds }: { listId: string; contactIds: string[] }) =>
      addMembers(listId, contactIds),
    onSuccess: () => {
      invalidateLists(qc)
      // The list's contacts table reads the contacts queries — refresh so a just-added member shows.
      qc.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}

export function useRemoveFromList() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ listId, contactId }: { listId: string; contactId: string }) =>
      removeMember(listId, contactId),
    onSuccess: () => {
      invalidateLists(qc)
      qc.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}

export function useSendToList() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ listId, input }: { listId: string; input: SendMessageInput }) =>
      sendToList(listId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['analytics', 'email'] }) // the dashboard's sent count
      invalidateLists(qc) // "last sent"
    },
  })
}
